import Spinner from "@/components/spinner";
import { Button } from "@/components/ui/button";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useRemoveEvidenceByIdMutation } from "@/hooks/remove-evidence-by-id.hook";
import { GetResponsesByQuizIdItemEvidence } from "@/requests/questionnaries/responses-by-quiz-id.request";
import { useQuestion } from "@/store/question.context";
import { AnswerMode, DimensionId } from "@/types/questionnaries";
import { Trash } from "iconsax-react";
import { FC, useState } from "react";
import EvidenceListResourceItem from "./evidence-list-resource-item";
import NewEvidenceDrawer from "./new-evidence-drawer";

type EdivenceListProps = {
  disabled?: boolean;
};

const numberOfAllowedEvidences = 3;

const EvidenceList: FC<EdivenceListProps> = (props) => {
  const { question, quizId } = useQuestion();

  const [showNewEvidenceDrawer, setShowNewEvidenceDrawer] = useState(false);

  const removeMutation = useRemoveEvidenceByIdMutation();

  const { data: userProfile } = useCurrentUserProfile();

  const handleNewEvidenceDrawerOpenChange = (isOpen: boolean) => {
    setShowNewEvidenceDrawer(isOpen);
  };

  const handleRemoveEvidence = (evidence: GetResponsesByQuizIdItemEvidence) => {
    removeMutation.mutate({
      companyId: userProfile?.companyId || "",
      quizId,
      responseId: question?.responseId || "",
      evidenceId: evidence.id,
      dimension: question?.dimension as DimensionId,
      topic: question?.topic || "",
    });
  };

  const evidences = Array.from(
    { length: numberOfAllowedEvidences },
    (_, i) => question?.evidences[i] ?? null,
  );

  return (
    <section className="my-5 flex flex-col items-start gap-4">
      <h5 className="text-sm font-bold text-[#293038]">Evidencias</h5>

      {!props.disabled && (
        <>
          <p className="text-sm">
            Adjunta hasta {numberOfAllowedEvidences} evidencias. Se requiere al
            menos una.
          </p>

          <NewEvidenceDrawer
            isOpen={showNewEvidenceDrawer}
            onOpenChange={handleNewEvidenceDrawerOpenChange}
            responseId={question?.responseId || ""}
          />
        </>
      )}

      {props.disabled && evidences.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No se adjuntaron evidencias.
        </p>
      )}

      {evidences.map((evidence, index) => (
        <div
          key={index}
          className="flex w-full gap-2 rounded-md bg-gray-50 p-2 text-sm"
        >
          <div className="flex w-full items-start gap-4">
            <div className="flex grow flex-col gap-2">
              <p className="text-xs font-bold text-[#615CB7]">
                {index === 0 && question?.answerMode === AnswerMode.Single && (
                  <>
                    {question.choices[0].selected ? (
                      <>
                        Evidencia obligatoria
                        {evidence === null && !props.disabled && (
                          <span className="text-destructive">
                            {" "}
                            (es necesario que ingreses una evidencia)
                          </span>
                        )}
                      </>
                    ) : question.choices[2].selected ? (
                      <>
                        Evidencia obligatoria
                        {evidence === null && !props.disabled && (
                          <span className="text-destructive">
                            {" "}
                            (adjunta una declaración jurada)
                          </span>
                        )}
                      </>
                    ) : (
                      "Evidencia complementaria"
                    )}
                  </>
                )}

                {index !== 0 && `Evidencia complementaria`}
              </p>

              {evidence ? (
                evidence.resources.map((evidenceResource) => (
                  <EvidenceListResourceItem
                    key={evidenceResource.id}
                    evidenceResource={evidenceResource}
                  />
                ))
              ) : (
                <p className="text-muted-foreground">Ninguna</p>
              )}
            </div>

            {!props.disabled && evidence && (
              <Button
                variant={"secondary"}
                size={"icon"}
                className="shrink-0"
                onClick={() => handleRemoveEvidence(evidence)}
                disabled={
                  removeMutation.variables?.evidenceId === evidence.id &&
                  removeMutation.isPending
                }
              >
                {removeMutation.variables?.evidenceId === evidence.id &&
                removeMutation.isPending ? (
                  <Spinner className="m-0" />
                ) : (
                  <Trash />
                )}
              </Button>
            )}
          </div>
        </div>
      ))}
    </section>
  );
};

export default EvidenceList;
